/**
 * Client Balance View for Clinician Portal
 * 
 * Shows calculated balance data with day/week/month aggregation
 * Integrates daily check-in data and WHOOP metrics
 */

import React, { useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Scale, TrendingUp, TrendingDown, Minus, Activity, Heart, 
  Brain, Sparkles, Users, Calendar, Info, AlertTriangle 
} from 'lucide-react';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, 
  PolarRadiusAxis, Radar, Legend 
} from 'recharts';
import { format, parseISO, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import { 
  BALANCE_DIMENSIONS, 
  calculateBalanceProfile, 
  BalanceProfile 
} from '@/lib/balanceFramework';

interface CheckinData {
  date: string;
  emotion?: string;
  energy_field?: string;
  body_feeling?: string;
  mind_state?: string;
  spiritual_connection?: string;
  relational_feeling?: string;
  hours_slept?: number;
  sleep_quality?: number;
  exercise_minutes?: number;
  fatigue_level?: number;
  stress_level?: number;
  anxiety_level?: number;
  mood_score?: number;
  // WHOOP data when available
  whoop_recovery_score?: number;
  whoop_hrv?: number;
  whoop_strain?: number;
  whoop_sleep_performance?: number;
  [key: string]: any;
}

interface WhoopData {
  date: string;
  recovery_score?: number;
  hrv_rmssd?: number;
  strain?: number;
  sleep_performance?: number;
}

interface ClientBalanceViewProps {
  checkins: CheckinData[];
  whoopData?: WhoopData[];
  clientName: string;
  timeRange: string;
}

const dimensionIcons: Record<string, React.ReactNode> = {
  physical: <Activity className="h-4 w-4 text-emerald-500" />,
  emotional: <Heart className="h-4 w-4 text-rose-500" />,
  mental: <Brain className="h-4 w-4 text-blue-500" />,
  spiritual: <Sparkles className="h-4 w-4 text-purple-500" />,
  relational: <Users className="h-4 w-4 text-amber-500" />
};

const dimensionColours: Record<string, string> = {
  physical: '#10b981',
  emotional: '#f43f5e',
  mental: '#3b82f6',
  spiritual: '#8b5cf6',
  relational: '#f59e0b'
};

export function ClientBalanceView({ checkins, whoopData, clientName, timeRange }: ClientBalanceViewProps) {
  // Merge WHOOP data into checkins by date
  const enrichedCheckins = useMemo(() => {
    if (!whoopData || whoopData.length === 0) return checkins;
    
    const whoopByDate = new Map<string, WhoopData>();
    whoopData.forEach(w => {
      const dateKey = w.date.split('T')[0];
      whoopByDate.set(dateKey, w);
    });
    
    return checkins.map(checkin => {
      const whoop = whoopByDate.get(checkin.date);
      if (whoop) {
        return {
          ...checkin,
          whoop_recovery_score: whoop.recovery_score,
          whoop_hrv: whoop.hrv_rmssd,
          whoop_strain: whoop.strain,
          whoop_sleep_performance: whoop.sleep_performance
        };
      }
      return checkin;
    });
  }, [checkins, whoopData]);

  // Calculate daily balance profiles
  const dailyBalances = useMemo(() => {
    return enrichedCheckins.map(checkin => {
      const profile = calculateBalanceProfile(checkin);
      return {
        date: checkin.date,
        displayDate: format(parseISO(checkin.date), 'dd MMM'),
        profile,
        hasWhoop: !!(checkin.whoop_recovery_score || checkin.whoop_hrv)
      };
    });
  }, [enrichedCheckins]);

  // Calculate period averages (latest, 7d, 30d)
  const periodStats = useMemo(() => {
    if (dailyBalances.length === 0) return null;

    const calcPeriodAvg = (days: number) => {
      const periodData = dailyBalances.slice(-days);
      if (periodData.length === 0) return null;

      const avgOverall = periodData.reduce((sum, d) => sum + d.profile.overallBalance, 0) / periodData.length;
      
      const dimensionAvgs = BALANCE_DIMENSIONS.map(dim => {
        const scores = periodData.map(d => {
          const score = d.profile.scores.find(s => s.dimensionId === dim.id);
          return score ? score.score : 0;
        });
        const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
        return { dimensionId: dim.id, avgScore: avg };
      });

      return { avgOverall: Math.round(avgOverall), dimensionAvgs, count: periodData.length };
    };

    return {
      today: dailyBalances.length > 0 ? dailyBalances[dailyBalances.length - 1] : null,
      week: calcPeriodAvg(7),
      month: calcPeriodAvg(30),
      all: calcPeriodAvg(dailyBalances.length)
    };
  }, [dailyBalances]);

  // Prepare radar chart data
  const radarData = useMemo(() => {
    if (!periodStats?.week) return [];

    return BALANCE_DIMENSIONS.map(dim => {
      const weekScore = periodStats.week?.dimensionAvgs.find(d => d.dimensionId === dim.id);
      const monthScore = periodStats.month?.dimensionAvgs.find(d => d.dimensionId === dim.id);
      
      // Convert -100 to +100 score to 0-100 for radar (50 = balanced)
      const normalise = (score: number) => 50 + (score / 2);
      
      return {
        dimension: dim.name,
        week: weekScore ? normalise(weekScore.avgScore) : 50,
        month: monthScore ? normalise(monthScore.avgScore) : 50
      };
    });
  }, [periodStats]);

  // Prepare trend data
  const trendData = useMemo(() => {
    return dailyBalances.map(d => ({
      date: d.displayDate,
      overall: Math.round(d.profile.overallBalance),
      physical: 50 + (d.profile.scores.find(s => s.dimensionId === 'physical')?.score || 0) / 2,
      emotional: 50 + (d.profile.scores.find(s => s.dimensionId === 'emotional')?.score || 0) / 2,
      mental: 50 + (d.profile.scores.find(s => s.dimensionId === 'mental')?.score || 0) / 2,
      spiritual: 50 + (d.profile.scores.find(s => s.dimensionId === 'spiritual')?.score || 0) / 2,
      relational: 50 + (d.profile.scores.find(s => s.dimensionId === 'relational')?.score || 0) / 2
    }));
  }, [dailyBalances]);

  // Calculate trends
  const trends = useMemo(() => {
    if (dailyBalances.length < 3) return null;

    const recent = dailyBalances.slice(-3);
    const earlier = dailyBalances.slice(0, Math.min(3, dailyBalances.length - 3));
    
    if (earlier.length === 0) return null;

    return BALANCE_DIMENSIONS.map(dim => {
      const recentAvg = recent.reduce((sum, d) => {
        const score = d.profile.scores.find(s => s.dimensionId === dim.id);
        return sum + (score ? Math.abs(score.score) : 0);
      }, 0) / recent.length;

      const earlierAvg = earlier.reduce((sum, d) => {
        const score = d.profile.scores.find(s => s.dimensionId === dim.id);
        return sum + (score ? Math.abs(score.score) : 0);
      }, 0) / earlier.length;

      const change = earlierAvg - recentAvg; // Positive = improving (less imbalance)
      
      return {
        id: dim.id,
        name: dim.name,
        trend: Math.abs(change) < 5 ? 'stable' : change > 0 ? 'improving' : 'declining',
        change: Math.round(change)
      };
    });
  }, [dailyBalances]);

  // Identify areas of concern
  const concerns = useMemo(() => {
    if (!periodStats?.week) return [];

    return periodStats.week.dimensionAvgs
      .filter(d => Math.abs(d.avgScore) > 30)
      .map(d => {
        const dim = BALANCE_DIMENSIONS.find(dim => dim.id === d.dimensionId);
        return {
          dimension: dim?.name || d.dimensionId,
          score: d.avgScore,
          tilt: d.avgScore < 0 ? dim?.leftPole : dim?.rightPole,
          severity: Math.abs(d.avgScore) > 50 ? 'significant' : 'moderate'
        };
      })
      .sort((a, b) => Math.abs(b.score) - Math.abs(a.score));
  }, [periodStats]);

  const getTrendIcon = (trend: string) => {
    if (trend === 'improving') return <TrendingUp className="h-3 w-3 text-emerald-500" />;
    if (trend === 'declining') return <TrendingDown className="h-3 w-3 text-rose-500" />;
    return <Minus className="h-3 w-3 text-muted-foreground" />;
  };

  if (checkins.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Scale className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
          <h3 className="font-semibold mb-2">No Balance Data Available</h3>
          <p className="text-sm text-muted-foreground">
            {clientName} hasn't completed any check-ins in this period.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Period Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Today/Latest */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Latest Check-in
            </CardTitle>
          </CardHeader>
          <CardContent>
            {periodStats?.today ? (
              <>
                <div className="text-3xl font-bold text-primary">
                  {Math.round(periodStats.today.profile.overallBalance)}%
                </div>
                <p className="text-xs text-muted-foreground">
                  {format(parseISO(periodStats.today.date), 'dd MMM yyyy')}
                  {periodStats.today.hasWhoop && (
                    <Badge variant="outline" className="ml-2 text-xs">WHOOP</Badge>
                  )}
                </p>
              </>
            ) : (
              <p className="text-muted-foreground">No data</p>
            )}
          </CardContent>
        </Card>

        {/* 7-Day Average */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              7-Day Average
            </CardTitle>
          </CardHeader>
          <CardContent>
            {periodStats?.week ? (
              <>
                <div className="text-3xl font-bold text-primary">
                  {periodStats.week.avgOverall}%
                </div>
                <p className="text-xs text-muted-foreground">
                  Based on {periodStats.week.count} check-ins
                </p>
              </>
            ) : (
              <p className="text-muted-foreground">Not enough data</p>
            )}
          </CardContent>
        </Card>

        {/* 30-Day Average */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              30-Day Average
            </CardTitle>
          </CardHeader>
          <CardContent>
            {periodStats?.month ? (
              <>
                <div className="text-3xl font-bold text-primary">
                  {periodStats.month.avgOverall}%
                </div>
                <p className="text-xs text-muted-foreground">
                  Based on {periodStats.month.count} check-ins
                </p>
              </>
            ) : (
              <p className="text-muted-foreground">Not enough data</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Areas of Concern */}
      {concerns.length > 0 && (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-4 w-4" />
              Areas Needing Attention
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {concerns.map(concern => (
                <Badge 
                  key={concern.dimension}
                  variant={concern.severity === 'significant' ? 'destructive' : 'outline'}
                  className="gap-1"
                >
                  {concern.dimension}: Excess {concern.tilt}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts */}
      <Tabs defaultValue="radar" className="space-y-4">
        <TabsList>
          <TabsTrigger value="radar">Balance Radar</TabsTrigger>
          <TabsTrigger value="trends">Trends Over Time</TabsTrigger>
          <TabsTrigger value="dimensions">By Dimension</TabsTrigger>
        </TabsList>

        <TabsContent value="radar">
          <Card>
            <CardHeader>
              <CardTitle>Balance Profile Comparison</CardTitle>
              <CardDescription>
                Comparing weekly vs monthly balance across all dimensions (50 = balanced)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarData}>
                    <PolarGrid stroke="hsl(var(--border))" />
                    <PolarAngleAxis 
                      dataKey="dimension" 
                      tick={{ fontSize: 12, fill: 'hsl(var(--foreground))' }}
                    />
                    <PolarRadiusAxis 
                      domain={[0, 100]} 
                      tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                    />
                    <Radar
                      name="7-Day Avg"
                      dataKey="week"
                      stroke="#d5c5a1"
                      fill="#d5c5a1"
                      fillOpacity={0.3}
                    />
                    <Radar
                      name="30-Day Avg"
                      dataKey="month"
                      stroke="hsl(var(--primary))"
                      fill="hsl(var(--primary))"
                      fillOpacity={0.1}
                    />
                    <Legend />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="trends">
          <Card>
            <CardHeader>
              <CardTitle>Overall Balance Over Time</CardTitle>
              <CardDescription>
                Tracking holistic balance percentage across check-ins
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    />
                    <YAxis 
                      domain={[0, 100]} 
                      tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--background))', 
                        border: '1px solid hsl(var(--border))' 
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="overall" 
                      stroke="#d5c5a1" 
                      strokeWidth={3}
                      dot={{ fill: '#d5c5a1', r: 4 }}
                      name="Overall Balance %"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="dimensions">
          <Card>
            <CardHeader>
              <CardTitle>Individual Dimension Trends</CardTitle>
              <CardDescription>
                Each dimension shown as deviation from balance (50 = balanced)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    />
                    <YAxis 
                      domain={[0, 100]} 
                      tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--background))', 
                        border: '1px solid hsl(var(--border))' 
                      }}
                    />
                    <Legend />
                    {BALANCE_DIMENSIONS.map(dim => (
                      <Line
                        key={dim.id}
                        type="monotone"
                        dataKey={dim.id}
                        stroke={dimensionColours[dim.id]}
                        strokeWidth={2}
                        dot={{ fill: dimensionColours[dim.id], r: 3 }}
                        name={dim.name}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dimension Breakdown with Trends */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Scale className="h-5 w-5" />
            Dimension Analysis
          </CardTitle>
          <CardDescription>
            QSP Framework breakdown with recent trends
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {BALANCE_DIMENSIONS.map(dim => {
              const weekScore = periodStats?.week?.dimensionAvgs.find(d => d.dimensionId === dim.id);
              const trend = trends?.find(t => t.id === dim.id);
              const score = weekScore?.avgScore || 0;
              const absScore = Math.abs(score);
              const status = absScore < 15 ? 'Balanced' : 
                             score < 0 ? `${dim.leftPole} excess` : `${dim.rightPole} excess`;

              return (
                <div 
                  key={dim.id} 
                  className="p-4 rounded-lg bg-muted/30 border border-border/50"
                >
                  <div className="flex items-center gap-2 mb-2">
                    {dimensionIcons[dim.id]}
                    <span className="font-medium text-sm">{dim.name}</span>
                  </div>
                  <div className={`text-lg font-bold ${
                    absScore < 15 ? 'text-emerald-500' : 
                    absScore < 30 ? 'text-amber-500' : 'text-rose-500'
                  }`}>
                    {status}
                  </div>
                  {trend && (
                    <div className="flex items-center gap-1 mt-1">
                      {getTrendIcon(trend.trend)}
                      <span className={`text-xs ${
                        trend.trend === 'improving' ? 'text-emerald-500' :
                        trend.trend === 'declining' ? 'text-rose-500' :
                        'text-muted-foreground'
                      }`}>
                        {trend.trend === 'stable' ? 'Stable' : 
                         trend.trend === 'improving' ? 'Improving' : 'Needs attention'}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Connected Self Philosophy Note */}
      <Card className="bg-muted/20">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-muted-foreground mt-0.5" />
            <div>
              <p className="text-sm font-medium">Connected Self Philosophy</p>
              <p className="text-xs text-muted-foreground mt-1">
                "What impacts one level impacts all." Balance isn't about perfection-it's about 
                awareness and adaptive adjustment. Each dimension influences the others; addressing 
                one area often creates positive ripples across all.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default ClientBalanceView;
